import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MonthYearPickerProps {
  value: string;  // Format: "YYYY-MM" or ""
  onChange: (value: string) => void;
  name: string;
  required?: boolean;
  className?: string;
  placeholder?: string;
}

export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value,
  onChange,
  name,
  required = false,
  className = '',
  placeholder = ''
}) => {
  const { t, i18n } = useTranslation();
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    if (!value) {
      setSelectedYear('');
      setSelectedMonth('');
      return;
    }

    const [year, month] = value.split('-');
    setSelectedYear(year || '');
    setSelectedMonth(month || '');
  }, [value]);
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // Month names follow the active UI language (#288). Previously this used
  // `toLocaleString('default', …)`, which follows the operating system /
  // browser locale and leaked Portuguese into the EN and ES interfaces.
  const monthLabel = (month: number) =>
    new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
      month: 'long',
    }).format(new Date(2000, month - 1));
  
  const handleYearChange = (newYear: string) => {
    setSelectedYear(newYear);

    if (newYear && selectedMonth) {
      onChange(`${newYear}-${selectedMonth.padStart(2, '0')}`);
      return;
    }

    if (!newYear && !selectedMonth) {
      onChange('');
    }
  };
  
  const handleMonthChange = (newMonth: string) => {
    setSelectedMonth(newMonth);

    if (selectedYear && newMonth) {
      onChange(`${selectedYear}-${newMonth.padStart(2, '0')}`);
      return;
    }

    if (!selectedYear && !newMonth) {
      onChange('');
    }
  };
  
  return (
    <div className="flex gap-2">
      <select
        name={`${name}_month`}
        value={selectedMonth}
        onChange={(e) => handleMonthChange(e.target.value)}
        required={required}
        className={`${className} flex-1 bg-white`}
      >
        <option value="">{t('common.month')}</option>
        {months.map(m => (
          <option key={m} value={m.toString().padStart(2, '0')}>
            {monthLabel(m)}
          </option>
        ))}
      </select>
      
      <select
        name={`${name}_year`}
        value={selectedYear}
        onChange={(e) => handleYearChange(e.target.value)}
        required={required}
        className={`${className} flex-1 bg-white`}
      >
        <option value="">{t('common.year')}</option>
        {years.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
};
